import { useState } from 'react';
import type { MatterMember } from '@/types/base';
import {Textarea} from "@/components/ui/textarea";
import type { DeadlineTagVocabularyItem } from '@/hooks/useTagVocabulary';
import {
  diffDeadlineTags,
  hasDeadlineTagChanges,
  type DeadlineTagChanges,
} from '@/lib/deadlines/tags';
import type { SyntheticTag, Task } from './columns-tasks';
import { getTaskStateConfig, normalizeTaskState, type TaskState } from '@/components/tasks/constants';
import { DatePicker } from './DatePicker';
import { DeadlineTagChips } from './deadline-tag-chips';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';
import { FancyMultiSelect } from './fancy-multi-select';
import { Input } from './input';
import { Label } from './label';
import { Button } from './NewButton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from './select';

type FormMode = 'create' | 'edit';

type TaskFormValues = {
  description: string;
  notes: string;
  date: Date;
  state: TaskState;
  assigned_to_guid: string | null;
  assigned_to_name: string | null;
  assigned_to_email: string | null;
  party: string | null;
};

interface TaskFormDialogProps {
  mode: FormMode;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (taskIdOrData: string | any, updates?: any) => void | Promise<void>;
  task?: Task | null;
  members?: MatterMember[];
  availableTasks?: Task[];
  /** Controlled tag vocabulary. Omit (with onSaveTags) to hide the Tags field. */
  tagVocabulary?: DeadlineTagVocabularyItem[];
  /** Persists the staged tag edits on save. Omit to hide the Tags field. */
  onSaveTags?: (syntheticId: string, changes: DeadlineTagChanges) => void | Promise<void>;
}

const toFormDate = (value: Date | string) => value instanceof Date ? value : new Date(value);
const toNullableText = (value: string | null | undefined) => value || null;

const getTaskInitialValues = (task: Task): TaskFormValues => ({
  description: task.description ?? '',
  notes: task.notes || '',
  date: toFormDate(task.date),
  state: normalizeTaskState(task.state),
  assigned_to_guid: task.assigned_to_guid || null,
  assigned_to_name: toNullableText(task.assigned_to_name),
  assigned_to_email: toNullableText(task.assigned_to_email),
  party: toNullableText(task.party),
});

const getChangedEditValues = (initialValues: TaskFormValues, currentValues: TaskFormValues) => {
  const updates: Partial<Task> = {};

  if (currentValues.description !== initialValues.description) {
    updates.description = currentValues.description;
  }
  if (currentValues.notes !== initialValues.notes) {
    updates.notes = currentValues.notes;
  }
  if (currentValues.date.getTime() !== initialValues.date.getTime()) {
    updates.date = currentValues.date;
  }
  if (currentValues.state !== initialValues.state) {
    updates.state = currentValues.state;
  }
  if (currentValues.assigned_to_guid !== initialValues.assigned_to_guid) {
    updates.assigned_to_guid = currentValues.assigned_to_guid;
    updates.assigned_to_name = currentValues.assigned_to_name || undefined;
    updates.assigned_to_email = currentValues.assigned_to_email || undefined;
  }
  if (currentValues.party !== initialValues.party) {
    updates.party = currentValues.party;
  }

  return updates;
};

export const TaskFormDialog = ({
  isOpen,
  mode,
  task,
  ...props
}: TaskFormDialogProps) => {
  if (!isOpen) return null;
  if (mode === 'edit' && !task) return null;

  const formKey = mode === 'edit' ? `edit-${task!.id}` : 'create-deadline';

  return (
    <TaskFormDialogInner
      key={formKey}
      isOpen={isOpen}
      mode={mode}
      task={task}
      {...props}
    />
  );
};

const TaskFormDialogInner = ({
  mode,
  isOpen,
  onClose,
  onSubmit,
  task,
  members = [],
  availableTasks = [],
  tagVocabulary = [],
  onSaveTags,
}: TaskFormDialogProps) => {
  const editTask = mode === 'edit' ? task! : null;
  const [initialValues] = useState<TaskFormValues>(() => editTask
    ? getTaskInitialValues(editTask)
    : {
        description: '',
        notes: '',
        date: new Date(),
        state: normalizeTaskState(undefined),
        assigned_to_guid: null,
        assigned_to_name: null,
        assigned_to_email: null,
        party: null,
      });

  const [description, setDescription] = useState(() => initialValues.description);
  const [notes, setNotes] = useState(() => initialValues.notes);
  const [date, setDate] = useState<Date | null>(() => initialValues.date);
  const [state, setState] = useState<TaskState>(() => initialValues.state);
  const [selectedAssigneeGuid, setSelectedAssigneeGuid] = useState<string>(() => initialValues.assigned_to_guid || 'none');
  const [party, setParty] = useState<string>(() => initialValues.party || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Tags are staged locally and only written on save, so Cancel discards them
  // like every other field. They live outside TaskFormValues because they are
  // persisted through their own endpoints, not the task update.
  const [savedTags] = useState<SyntheticTag[]>(() => editTask?.tags ?? []);
  const [stagedTags, setStagedTags] = useState<SyntheticTag[]>(savedTags);

  const isEditMode = mode === 'edit';
  // Only a synthetic-backed deadline can hold tags; legacy rows have no
  // synthetic, and creation has no id to attach them to yet.
  const syntheticDeadlineId = editTask?.synthetic_deadline_id ?? null;
  const canEditTags =
    isEditMode && !!syntheticDeadlineId && !!onSaveTags && tagVocabulary.length > 0;

  const addStagedTag = (label: string) =>
    setStagedTags((current) =>
      current.some((tag) => tag.label === label)
        ? current
        : [...current, { id: '', label, origin: 'manual' }],
    );
  const removeStagedTag = (removed: SyntheticTag) =>
    setStagedTags((current) => current.filter((tag) => tag.label !== removed.label));

  const selectedAssignee = selectedAssigneeGuid === 'none'
    ? null
    : members.find((member) => member.user_guid === selectedAssigneeGuid);
  const selectedAssigneeLabel = selectedAssigneeGuid === 'none'
    ? 'Unassigned'
    : selectedAssignee?.name || editTask?.assigned_to_name || 'Select assignee (optional)';

  // Get unique parties from available tasks
  const availableParties = Array.from(
    new Set(
      availableTasks
        .map(t => t.party)
        .filter((p): p is string => !!p && p !== 'Unknown')
    )
  ).sort();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!description || !date || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const assigneeGuid = selectedAssigneeGuid === 'none' ? null : selectedAssigneeGuid;
      const assignee = assigneeGuid ? members.find(m => m.user_guid === assigneeGuid) : null;
      const partyValue = party.trim() || null;
      const currentValues: TaskFormValues = {
        description,
        notes,
        date,
        state: normalizeTaskState(state),
        assigned_to_guid: assigneeGuid,
        assigned_to_name: assignee?.name || (assigneeGuid === initialValues.assigned_to_guid ? initialValues.assigned_to_name : null),
        assigned_to_email: assignee?.email || (assigneeGuid === initialValues.assigned_to_guid ? initialValues.assigned_to_email : null),
        party: partyValue,
      };

      const formData = {
        description: currentValues.description,
        notes: currentValues.notes,
        date: currentValues.date,
        assigned_to_guid: currentValues.assigned_to_guid,
        assigned_to_name: currentValues.assigned_to_name,
        assigned_to_email: currentValues.assigned_to_email,
        party: currentValues.party,
      };

      if (isEditMode && editTask) {
        await onSubmit(editTask.id, getChangedEditValues(initialValues, currentValues));

        if (canEditTags && syntheticDeadlineId && onSaveTags) {
          const tagChanges = diffDeadlineTags(savedTags, stagedTags);
          if (hasDeadlineTagChanges(tagChanges)) {
            await onSaveTags(syntheticDeadlineId, tagChanges);
          }
        }
        onClose();
      } else {
        await onSubmit(formData);
      }
    } catch {
      // Re-enable the form so a failed save can be retried; the action hooks
      // have already surfaced the reason via toast. On success isSubmitting is
      // deliberately left set, so the closing dialog can't be double-submitted.
      setIsSubmitting(false);
    }
  };

  // Get available states for dropdown (edit mode only)
  const getAvailableStates = (): TaskState[] => {
    if (!isEditMode || !editTask) return ['Confirmed', 'Completed'];

    const states: TaskState[] = [];
    const isExtracted = !editTask.created_by;

    if (isExtracted || state === 'Extracted') {
      states.push('Extracted');
    }

    states.push('Confirmed', 'Completed');
    return states;
  };

  const dialogTitle = isEditMode ? 'Edit deadline' : 'Create new deadline';
  const dialogDescription = isEditMode
    ? 'Update the deadline details and status'
    : 'Create a new deadline with a due date and description';
  const submitButtonText = isEditMode ? 'Save changes' : 'Create deadline';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogDescription>{dialogDescription}</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="description">Deadline description *</Label>
              <Input
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter deadline description"
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="party">Responsible party</Label>
              <FancyMultiSelect
                singleSelect
                freeText
                value={party ? [{ value: party, label: party }] : []}
                onValueChange={(values) => setParty(values[0]?.value || '')}
                options={availableParties.map(p => ({ value: p, label: p }))}
                placeholder="Type or select party (optional)"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="date">Due date *</Label>
              <DatePicker
                initialValue={date ?? undefined}
                onChange={(newDate) => {
                  setDate(newDate ?? null);
                }}
                triggerClassName="w-full"
              />
            </div>

            {canEditTags && (
              <div className="grid gap-2">
                <Label>Tags</Label>
                <DeadlineTagChips
                  tags={stagedTags}
                  vocabulary={tagVocabulary}
                  editable
                  onAdd={addStagedTag}
                  onRemove={removeStagedTag}
                />
                {stagedTags.length > 0 && (
                  <p className="text-xs text-gray-500">
                    Click a tag you added to remove it. AI-suggested tags cannot be removed.
                  </p>
                )}
              </div>
            )}

            <div className="grid gap-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add additional notes (optional)"
                rows={3}
              />
            </div>

            {isEditMode && (
              <div className="grid gap-2">
                <Label htmlFor="state">Status *</Label>
                <Select value={state} onValueChange={(value) => setState(value as TaskState)}>
                  <SelectTrigger id="state">
                    <span>{getTaskStateConfig(state).label}</span>
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableStates().map((stateOption) => (
                      <SelectItem key={stateOption} value={stateOption}>
                        {getTaskStateConfig(stateOption).label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid gap-2">
              <Label htmlFor="assignee">Assignee</Label>
              <Select value={selectedAssigneeGuid} onValueChange={setSelectedAssigneeGuid}>
                <SelectTrigger id="assignee">
                  <span>{selectedAssigneeLabel}</span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {members.map((member) => (
                    <SelectItem key={member.user_guid} value={member.user_guid}>
                      {member.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting} text='Cancel' />
            <Button type="submit" disabled={!description || !date || isSubmitting} loading={isSubmitting} loadingText='Saving' text={submitButtonText} />
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
